import { app } from '@/bootloader';
import { DirectiveBinding } from 'vue';

/* Creating a map of all the focusable elements in the component and then adding a listener to each of
them. */
const componentTraps = new Map<
  string,
  {
    el: Element;
    // eslint-disable-next-line no-unused-vars
    listener: (e: any) => void;
  }[]
>();

const isShiftTabPressed = (e: any) => e.shiftKey && e.keyCode === 9;
const isTabPressed = (e: any) => e.keyCode === 9;

const directiveHandler = (el: HTMLElement, binding: DirectiveBinding<any>) => {
  const focusable = el?.querySelectorAll('button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
  const id = binding.value;
  if (focusable?.length) {
    /* Removing the event listener from the previous elements. */
    const cachedTrap = componentTraps.get(id);
    if (cachedTrap) {
      cachedTrap.forEach(trap => {
        trap.el.removeEventListener('keydown', trap.listener);
      });
    }
    /* Creating a map of all the focusable elements in the component and then adding a listener to each of
         them. */
    componentTraps.set(
      id,
      (Array.from(focusable) as Array<HTMLElement>).map((focusableEl, index) => ({
        el: focusableEl,
        listener: (e: any) => {
          /* This is to prevent the user from tabbing out of the component if there is only one focusable
               element. */
          if (focusable.length === 1 && isTabPressed(e)) {
            e.preventDefault();
            return;
          }
          /* This is the first element in the list of focusable elements. If the user presses the tab key, we
               want to focus on the next element. If the user presses the shift + tab key, we want to focus on the
               last element. */
          if (index === 0) {
            if (isShiftTabPressed(e)) {
              e.preventDefault();
              (focusable[focusable.length - 1] as HTMLElement).focus();
            } else if (isTabPressed(e) && focusable[index + 1]) {
              e.preventDefault();
              (focusable[index + 1] as HTMLElement).focus();
            }
          }
          //
          /* This is the last element in the list of focusable elements. If the user presses the tab key, we want
               to focus on the first element. If the user presses the shift + tab key, we want to focus on the
               previous element. */
          else if (index === focusable.length - 1) {
            if (isShiftTabPressed(e) && focusable[index - 1]) {
              e.preventDefault();
              (focusable[index - 1] as HTMLElement).focus();
            } else if (isTabPressed(e)) {
              e.preventDefault();
              (focusable[0] as HTMLElement)?.focus();
            }
          }
          //
          /* This is the logic for the tab key. If the user presses the tab key, we want to focus on the next
               element. If the user presses the shift + tab key, we want to focus on the previous element. */
          else if (isShiftTabPressed(e)) {
            e.preventDefault();
            (focusable[index - 1] as HTMLElement)?.focus();
          } else if (isTabPressed(e)) {
            e.preventDefault();
            (focusable[index + 1] as HTMLElement)?.focus();
          }
        },
      })),
    );

    /* Adding the event listener to each of the focusable elements in the component. */
    const newTrap = componentTraps.get(id);
    newTrap?.forEach(trap => {
      trap.el.addEventListener('keydown', trap.listener);
    });
  }
};

app.directive('incredi-focus-trap', {
  mounted(el, binding) {
    directiveHandler(el, binding);
  },
  updated(el, binding) {
    directiveHandler(el, binding);
  },
  unmounted(el, binding) {
    const id = binding.value;
    componentTraps.delete(id);
  },
});
